/**
 * The signed-in user's own Drive connection.
 *
 * One fetch answers both questions a surface has before it can render a Drive
 * export item: is the feature on, and has this person connected. Keeping them in
 * one payload is why there is no separate feature-flag call here.
 *
 * Connecting is a **top-level navigation**, not a popup. There is nothing for a
 * blocker to stop, and the surfaces that use it already keep their state in the
 * URL (the open artifact, the selected file, the thread), so `returnTo` puts the
 * user back exactly where they were.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, type GoogleDriveConnectionInfo } from '../shared/api-client';

export interface GoogleDriveConnectionState {
  loading: boolean;
  connected: boolean;
  googleUserEmail: string | null;
  connectedAt: string | null;
  /** DRIVE_DOC_EXPORT. When false, no Drive item should be rendered at all. */
  exportEnabled: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  startConnect: (returnTo: string) => void;
  disconnect: () => Promise<boolean>;
}

export function useGoogleDriveConnection(): GoogleDriveConnectionState {
  const [info, setInfo] = useState<GoogleDriveConnectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await api.getGoogleDriveConnection();
    if (result.success) {
      setInfo(result.data);
      setError(null);
    } else {
      setError(result.error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const startConnect = useCallback((returnTo: string) => {
    window.location.assign(`/auth/google-drive-user?returnTo=${encodeURIComponent(returnTo)}`);
  }, []);

  const disconnect = useCallback(async () => {
    const result = await api.disconnectGoogleDrive();
    if (!result.success) {
      setError(result.error.message);
      return false;
    }
    await refresh();
    return true;
  }, [refresh]);

  return {
    loading,
    connected: info?.connected ?? false,
    googleUserEmail: info?.googleUserEmail ?? null,
    connectedAt: info?.connectedAt ?? null,
    exportEnabled: info?.exportEnabled ?? false,
    error,
    refresh,
    startConnect,
    disconnect,
  };
}

/** Where a Drive connect flow should return to: this page, with its state intact. */
export function currentReturnTo(): string {
  return `${window.location.pathname}${window.location.search}`;
}
